<template>
  <div>
    <!-- Login Screen -->
    <div v-if="!user" class="min-h-screen flex items-center justify-center p-4 relative bg-background font-body overflow-hidden">
      <!-- Grid background -->
      <div class="fixed inset-0 z-0 opacity-[0.12] pointer-events-none"
           style="background-image: linear-gradient(#648F47 1px, transparent 1px), linear-gradient(90deg, #648F47 1px, transparent 1px); background-size: 50px 50px;"></div>

      <main class="relative z-10 w-full max-w-[1100px] grid md:grid-cols-2 bg-surface rounded-lg shadow-2xl overflow-hidden border border-outline-variant/30">
        <!-- Branding Panel -->
        <section class="hidden md:flex flex-col justify-center items-center p-12 bg-white relative overflow-hidden border-r border-outline-variant/20">
          <img alt="Wiązary Śmiatacz" :src="logoUrl" class="w-full max-w-[400px] h-auto object-contain z-10" />
          <div class="absolute -right-20 -bottom-20 opacity-[0.03] pointer-events-none w-[400px] h-[400px] bg-contain bg-no-repeat bg-center"
               :style="{ backgroundImage: `url(${logoUrl})` }"></div>
        </section>

        <!-- Login Form -->
        <section class="p-8 md:p-16 flex flex-col justify-center bg-white">
          <div class="max-w-sm mx-auto w-full">
            <div class="md:hidden flex justify-center mb-10">
              <img alt="Wiązary Śmiatacz" :src="logoUrl" class="h-12 w-auto object-contain" />
            </div>
            <div class="mb-10">
              <h2 class="font-headline text-2xl font-bold text-secondary mb-2">Logowanie</h2>
              <p class="text-on-surface-variant text-sm">CRM</p>
            </div>
            <form @submit.prevent="handleSubmit" class="space-y-5">
              <div class="space-y-1.5">
                <label for="email" class="text-[12px] font-semibold text-secondary/70 uppercase tracking-wide ml-0.5">E-mail</label>
                <div class="relative">
                  <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary/40 text-sm"><i class="pi pi-user"></i></span>
                  <input id="email" type="email" v-model="email" placeholder="uzytkownik@firma.pl" autocomplete="email"
                    class="w-full bg-white border border-outline-variant/60 rounded py-3.5 pl-11 pr-4 text-secondary placeholder:text-secondary/30 text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all" />
                </div>
              </div>
              <div class="space-y-1.5">
                <label for="password" class="text-[12px] font-semibold text-secondary/70 uppercase tracking-wide ml-0.5">Hasło</label>
                <div class="relative">
                  <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary/40 text-sm"><i class="pi pi-lock"></i></span>
                  <input id="password" type="password" v-model="password" placeholder="••••••••" autocomplete="current-password"
                    class="w-full bg-white border border-outline-variant/60 rounded py-3.5 pl-11 pr-4 text-secondary placeholder:text-secondary/30 text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all" />
                </div>
              </div>
              <div class="flex items-center gap-2.5 py-1">
                <input id="remember" type="checkbox" class="w-4 h-4 rounded border-outline-variant accent-primary cursor-pointer" />
                <label for="remember" class="text-xs text-on-surface-variant cursor-pointer select-none">Zapamiętaj mnie na tym urządzeniu</label>
              </div>
              <div v-if="error" class="bg-error-container text-on-error-container px-3.5 py-2.5 rounded text-[13px] font-medium">{{ error }}</div>
              <button type="submit" :disabled="isPending"
                class="w-full py-4 bg-primary hover:bg-primary-hover disabled:bg-primary-disabled text-white font-headline font-bold text-base rounded shadow-md hover:shadow-lg active:translate-y-[1px] transition-all duration-200 mt-4 flex items-center justify-center gap-2 group">
                <span v-if="isPending">LOGOWANIE...</span>
                <span v-else>ZALOGUJ SIĘ</span>
                <i v-if="!isPending" class="pi pi-arrow-right text-sm group-hover:translate-x-1 transition-transform"></i>
              </button>
            </form>
          </div>
        </section>
      </main>
    </div>

    <!-- Main App Layout -->
    <div v-else class="min-h-screen bg-surface font-body">
      <!-- Topbar -->
      <AppTopBar @menu-toggle="onMenuToggle" />

      <!-- Sidebar -->
      <aside
        :class="[
          'fixed top-14 left-0 bottom-0 w-64 bg-surface-container-lowest border-r border-surface-container-high z-[998] overflow-y-auto transition-transform duration-200',
          sidebarVisible ? 'translate-x-0' : '-translate-x-full'
        ]"
        @click="onSidebarClick"
      >
        <!-- Logo -->
        <router-link to="/" class="block px-5 pt-5 pb-3 flex-shrink-0">
          <img alt="Wiązary Śmiatacz" :src="logoUrl" class="w-full h-auto object-contain" />
        </router-link>

        <AppProfile />
        <AppMenu :model="menu" @menuitem-click="onMenuItemClick" />
      </aside>

      <!-- Mobile overlay -->
      <div
        v-if="mobileMenuActive"
        class="fixed inset-0 bg-black/30 z-[997] lg:hidden"
        @click="mobileMenuActive = false"
      ></div>

      <!-- Main content -->
      <main
        :class="[
          'pt-14 min-h-screen transition-all duration-200',
          sidebarVisible && isDesktop ? 'lg:ml-64' : 'ml-0'
        ]"
      >
        <div class="p-0">
          <router-view />
        </div>
        <AppFooter />
      </main>
    </div>

    <ConfirmDialog />
  </div>
</template>

<script>
import { ref, computed, onUnmounted } from 'vue';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { useToast } from 'primevue/usetoast';
import { useRouter } from 'vue-router';

import AppTopBar from '@/components/AppTopBar.vue';
import AppProfile from '@/components/AppProfile.vue';
import AppMenu from '@/components/AppMenu.vue';
import AppFooter from '@/components/AppFooter.vue';
// Import the asset so webpack rewrites it to the hashed /img/ URL. A literal
// "@/assets/logo.png" in a static src — and especially inside an inline
// `style="background-image: url(...)"` — is NOT processed by the loader and
// leaks the raw "@/" to the browser (GET /@/assets/logo.png -> 404).
import logoUrl from '@/assets/logo.png';

export default {
  name: 'App',
  components: { AppTopBar, AppProfile, AppMenu, AppFooter },
  setup() {
    const auth = getAuth();
    const router = useRouter();
    const toast = useToast();

    const user = ref(null);
    const email = ref('');
    const password = ref('');
    const error = ref(null);
    const isPending = ref(false);

    const unsubAuth = onAuthStateChanged(auth, (u) => { user.value = u; });
    onUnmounted(unsubAuth);

    const handleSubmit = async () => {
      error.value = null;
      isPending.value = true;
      try {
        await signInWithEmailAndPassword(auth, email.value, password.value);
        toast.add({ severity: 'success', summary: 'Zalogowano', detail: 'Pomyślnie zalogowano', life: 3000 });
        router.push({ name: 'OfferList' });
      } catch (err) {
        if (err.code === 'auth/web-storage-unsupported' || err.code === 'auth/invalid-user-token') {
          error.value = 'Safari blokuje dane sesji. Wyłącz tryb prywatny lub użyj Chrome.';
        } else {
          error.value = 'Nieprawidłowy email lub hasło';
        }
        toast.add({ severity: 'error', summary: 'Błąd logowania', detail: error.value, life: 5000 });
      } finally {
        isPending.value = false;
      }
    };

    const staticMenuInactive = ref(false);
    const mobileMenuActive = ref(false);
    const menuClick = ref(false);

    const menu = [
      { label: 'Strona główna', icon: 'pi pi-fw pi-home', to: { name: 'Home' } },
      {
        label: 'Konstruktor', role: ['konstruktor','sprzedawca','admin'], icon: 'pi pi-fw pi-inbox',
        items: [
          { label: 'Lista ofert', icon: 'pi pi-fw pi-inbox', to: { name: 'OfferList' }, role:['konstruktor','sprzedawca','admin'] },
          { label: 'Zamówienia zrealizowane', icon: 'pi pi-fw pi-folder', to: { name: 'DoneList' }, role:['konstruktor','sprzedawca','admin'] },
          { label: 'Moja Lista zamówień', icon: 'pi pi-fw pi-list', to: { name: 'MyList' }, role:['konstruktor','admin'] },
          { label: 'Dodaj ofertę', icon: 'pi pi-fw pi-plus', to: { name: 'CreateOffer' }, role:['konstruktor','sprzedawca','admin'] }
        ]
      },
      {
        label: 'Sprzedawca', icon: 'pi pi-fw pi-briefcase', role:['sprzedawca','admin','konstruktor'],
        items: [ { label: 'Lista ofert', icon: 'pi pi-fw pi-list', to: { name: 'OrdersList' }, role:['sprzedawca','admin','konstruktor'] } ]
      },
      {
        label: 'Produkcja', icon: 'pi pi-fw pi-directions', role:['produkcja','admin'],
        items: [ { label: 'Lista Zleceń', icon: 'pi pi-fw pi-list', to: { name: 'ProductionList' }, role:['produkcja','admin'] } ]
      },
      { label: 'Plan Produkcji', icon: 'pi pi-calendar-plus', to: { name: 'ProductionPlan' }, role:['konstruktor','sprzedawca','produkcja','admin'] },
      {
        label: 'Admin', icon: 'pi pi-cog', role:['sprzedawca','admin'],
        items: [
          { label: 'Ekipy', icon: 'pi pi-fw pi-users', to: { name: 'Teams' }, role:['sprzedawca','admin'] },
          { label: 'VAT', icon: 'pi pi-fw pi-tags', to: { name: 'Vat' }, role:['sprzedawca','admin'] },
          { label: 'Raport ofert', icon: 'pi pi-fw pi-chart-bar', to: { name: 'OffersReport' }, role:['admin'] },
          { label: 'Analityka', icon: 'pi pi-fw pi-chart-pie', to: { name: 'Dashboard' }, role:['admin'] },
          { label: 'Migracja', icon: 'pi pi-fw pi-sync', to: { name: 'MigrationAdmin' }, role:['admin'], email:['kamildudek6@gmail.com'] }
        ]
      }
    ];

    const isDesktop = computed(() => typeof window !== 'undefined' && window.innerWidth > 1024);

    const sidebarVisible = computed(() => {
      if (isDesktop.value) return !staticMenuInactive.value;
      return mobileMenuActive.value;
    });

    const onWrapperClick = () => {
      if (!menuClick.value) { mobileMenuActive.value = false; }
      menuClick.value = false;
    };

    const onMenuToggle = (event) => {
      menuClick.value = true;
      if (isDesktop.value) {
        staticMenuInactive.value = !staticMenuInactive.value;
      } else {
        mobileMenuActive.value = !mobileMenuActive.value;
      }
      event.preventDefault();
    };

    const onSidebarClick = () => { menuClick.value = true; };

    const onMenuItemClick = (event) => {
      if (event.item && !event.item.items) {
        mobileMenuActive.value = false;
      }
    };

    return {
      user, email, password, error, isPending, handleSubmit,
      menu, onWrapperClick, onMenuToggle, onSidebarClick,
      onMenuItemClick, sidebarVisible, mobileMenuActive, isDesktop,
      logoUrl
    };
  }
};
</script>
